'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { ActiveOrder } from '@/lib/api/history/models';
import { useCancelLimitOrder } from '@/lib/api/order/hooks';
import {
  useTradingActiveOrders,
  useTradingSharesBalance,
} from '@/lib/components/Trading/TradingDataContext';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { SHARE_DECIMALS } from '@/lib/utils/calculateFees';
import { formatCentsValue, formatMoney } from '@/lib/utils/formatAmount';
import { EventOpenOrdersCard } from './EventOpenOrdersCard';

// Map ActiveOrder to card format
function mapOrderToCard(
  order: ActiveOrder,
  outcomeName: string,
  resolutions?: Array<{ id: string; name: string; isPositive: boolean }>,
) {
  // Determine Yes/No based on assetId (resolution ID)
  let outcome: 'Yes' | 'No' = 'Yes'; // Default fallback
  if (resolutions && order.assetId) {
    const resolution = resolutions.find((r) => r.id === order.assetId);
    if (resolution) {
      // Use isPositive flag to determine Yes/No
      outcome = resolution.isPositive ? 'Yes' : 'No';
    }
  } else {
    // Fallback to price-based determination if assetId or resolutions are not available
    outcome = order.price > 0.5 ? 'Yes' : 'No';
  }

  // Convert price from decimal (0.58) to cents string (58¢)
  const priceInCents = Number(formatCentsValue(order.price * 100));

  // Plain, ungrouped: the request string is edited inline in the card and parsed
  // back (`parseInt`), so a thousands separator would truncate the quantity.
  const filled = formatMoney(order.quantity, { decimals: SHARE_DECIMALS, plain: true });
  const request = formatMoney(order.originalQuantity, { decimals: SHARE_DECIMALS, plain: true });

  // Calculate order value: Price * (Request - Filled) / 100
  const requestMinusFilled = order.originalQuantity - order.quantity;
  const orderValue = (priceInCents * requestMinusFilled) / 100;

  // Format expiration date or show "-" if null
  let expiration = '-';
  if (order.expireAt) {
    try {
      const expireDate = new Date(order.expireAt);
      const hours = expireDate.getUTCHours();
      const minutes = expireDate.getUTCMinutes();
      const seconds = expireDate.getUTCSeconds();

      // Check if it's end of day (23:59:59 or 23:59) AND it's today
      const today = new Date();
      const isToday =
        expireDate.getUTCDate() === today.getUTCDate() &&
        expireDate.getUTCMonth() === today.getUTCMonth() &&
        expireDate.getUTCFullYear() === today.getUTCFullYear();

      if (hours === 23 && minutes === 59 && (seconds === 59 || seconds === 0) && isToday) {
        expiration = 'End of the day';
      } else {
        const day = expireDate.getDate();
        const month = expireDate.toLocaleString('en-US', { month: 'long' });
        const year = expireDate.getFullYear();
        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');
        expiration = `${day} ${month} ${year}, ${hoursStr}:${minutesStr} UTC`;
      }
    } catch {
      expiration = '-';
    }
  }

  return {
    id: order.id,
    candidate: outcomeName,
    outcome,
    side: order.side === 'BUY' ? ('Buy' as const) : ('Sell' as const),
    price: `${priceInCents}¢`,
    filled,
    request,
    orderValue: formatMoney(orderValue, { prefix: '$' }),
    expiration,
    originalOrder: order,
  };
}

export const EventOpenOrdersList = () => {
  const tToasts = useTranslations('toasts.orders');
  const outcome = useSelectedOutcome();
  const event = useOrderFormStore.use.event();
  const { coins } = useCoins();
  const outcomeId = outcome?.id ?? '';
  const outcomeName = outcome?.question || 'Outcome';
  const resolutions = outcome?.r;

  // Get USDC assetId
  const quoteAssetId = useMemo(() => {
    const usdcCoin = coins.find((coin) => coin.shortName.toUpperCase() === 'USDC');
    return usdcCoin?.id || '';
  }, [coins]);

  // Get active orders from context
  const activeOrdersData = useTradingActiveOrders(outcomeId);
  const cancelLimitOrderMutation = useCancelLimitOrder();

  // Get shares balances for validation (for SELL orders)
  const yesSharesBalance = useTradingSharesBalance(outcomeId, 'yes');
  const noSharesBalance = useTradingSharesBalance(outcomeId, 'no');

  // Active orders are already filtered by API (status: ACTIVE)
  const activeOrders = useMemo(() => {
    return activeOrdersData?.items ?? [];
  }, [activeOrdersData]);

  // Map orders to cards
  const orders = useMemo(() => {
    return activeOrders.map((order) => mapOrderToCard(order, outcomeName, resolutions));
  }, [activeOrders, outcomeName, resolutions]);

  // Cancel order handler
  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      try {
        await cancelLimitOrderMutation.mutateAsync(orderId);
        toast.success(tToasts('cancelled'));
      } catch {
        // Error handled by global MutationCache onError
      }
    },
    [cancelLimitOrderMutation, tToasts],
  );

  if (orders.length === 0) {
    return null;
  }

  if (!event) {
    return null;
  }

  return (
    <div className="flex max-h-[270px] flex-col gap-2 overflow-y-auto">
      {orders.map((order) => (
        <EventOpenOrdersCard
          key={order.id}
          order={order}
          eventId={event.id}
          outcomeId={outcomeId}
          resolutions={resolutions}
          quoteAssetId={quoteAssetId}
          yesSharesBalance={yesSharesBalance}
          noSharesBalance={noSharesBalance}
          onCancel={() => handleCancelOrder(order.id)}
        />
      ))}
    </div>
  );
};
